import React from 'react';
import { ErrorMessage } from 'components';
import styled from 'styled-components';
import { AnimatePresence, motion, Reorder } from 'framer-motion';
import TextInput from 'components/Events/Livestream/Interaction/Polls/Shared/TextInput';
import { Tick } from 'assets/dynamic-svgs';
import { fadeInAndOutAndAnimateHeightVariants } from 'styles';

function Answer({
  answer,
  answersRef,
  arr,
  currentlyHoveredAnswer,
  handleAnswerCheckboxChange,
  handleAnswerTextChange,
  handleDeleteAnswer,
  i,
  isMarkCorrectAnswersEnabled,
  selectedEvent,
  setCurrentlyHoveredAnswer
}) {
  const showMarkCorrectAnswerIcon =
    isMarkCorrectAnswersEnabled &&
    arr.filter((_answer) => _answer.text && _answer.isCorrect).length < 1 &&
    answer.text &&
    !answer.isCorrect;

  const showReorderAnswerIcon =
    arr.length > 1 && currentlyHoveredAnswer === answer.id && answer.text;

  const showDeleteAnswerIcon = arr.length > 2 && currentlyHoveredAnswer === answer.id;

  return (
    <Container
      layout
      value={answer}
      drag="y"
      id={answer.id}
      dragConstraints={answersRef}
      dragElastic={0.0625}
      onMouseEnter={() => setCurrentlyHoveredAnswer(answer.id)}
      onMouseLeave={() => setCurrentlyHoveredAnswer(null)}>
      <AnimatePresence>
        {showReorderAnswerIcon && !showMarkCorrectAnswerIcon && (
          <ReorderAnswer role="button" tabIndex="0">
            <svg
              width="6"
              height="10"
              viewBox="0 0 6 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <circle cx="1" cy="1" r="1" fill="#4F4F4F" />
              <circle cx="1" cy="5" r="1" fill="#4F4F4F" />
              <circle cx="1" cy="9" r="1" fill="#4F4F4F" />
              <circle cx="5" cy="1" r="1" fill="#4F4F4F" />
              <circle cx="5" cy="5" r="1" fill="#4F4F4F" />
              <circle cx="5" cy="9" r="1" fill="#4F4F4F" />
            </svg>
          </ReorderAnswer>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showMarkCorrectAnswerIcon && (
          <RedArrow viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.701 1l3.211 3.195-3.21 3.194M1 4l9 .195" stroke="#F55151" />
          </RedArrow>
        )}
      </AnimatePresence>
      <AnswerCheckboxInput
        onClick={(e) => isMarkCorrectAnswersEnabled && answer.text && handleAnswerCheckboxChange(e)}
        isMarkCorrectAnswersEnabled={isMarkCorrectAnswersEnabled}
        isCorrect={answer.isCorrect}
        answerText={answer.text}
        selectedEvent={selectedEvent}>
        <div>
          <AnimatePresence>
            {answer.isCorrect && <Tick color={selectedEvent?.colors.secondary} />}
          </AnimatePresence>
        </div>
      </AnswerCheckboxInput>
      <div style={{ position: 'relative' }}>
        <AnswerTextInput
          onChange={(e) => handleAnswerTextChange(e, answer.id)}
          placeholder="Add answer"
          selectedEvent={selectedEvent}
          type="input"
          value={answer.text}
          error={
            answer.error?.code === 'missing-answer' &&
            ((arr.length <= 2 && !answer.text) ||
              (arr.length > 2 && !answer.text && i !== arr.length - 1))
          }
        />
        <ErrorMessage
          errorMessage={answer.error?.code === 'missing-answer' && answer.error.message}
          style={{
            color: '#F55151',
            fontSize: '0.75rem',
            position: 'absolute',
            top: '1.8rem',
            left: '0.4rem'
          }}
          variants={fadeInAndOutAndAnimateHeightVariants()}
        />
      </div>
      <AnimatePresence>
        {showDeleteAnswerIcon && (
          <DeleteAnswerWrapper onClick={handleDeleteAnswer} role="button" tabIndex="0">
            <svg
              width="8"
              height="8"
              viewBox="0 0 8 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M7 1L0.999999 7" stroke="#4F4F4F" strokeWidth="2" />
              <path d="M7 7L0.999999 0.999998" stroke="#4F4F4F" strokeWidth="2" />
            </svg>
          </DeleteAnswerWrapper>
        )}
      </AnimatePresence>
    </Container>
  );
}

const Container = styled(Reorder.Item).attrs({
  variants: {
    initial: {
      scale: 0,
      opacity: 0
    },
    animate: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.9,
        type: 'spring'
      }
    },
    exit: {
      scale: 0,
      opacity: 0,
      transition: {
        duration: 0.6,
        type: 'tween'
      }
    }
  },
  initial: 'initial',
  animate: 'animate',
  exit: 'exit'
})`
  left: -0.375rem;
  padding-bottom: 1.125rem;
  padding-left: 1.85rem;
  position: relative;
`;

const RedArrow = styled(motion.svg).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1, x: [0, 4, 0] },
  transition: {
    x: {
      repeat: Infinity,
      repeatType: 'loop',
      duration: 0.8
    }
  },
  exit: { opacity: 0 }
})`
  left: -0.875rem;
  position: absolute;
  top: 0.563rem;
  width: 0.625rem;
`;

const AnswerCheckboxInput = styled.div`
  cursor: ${({ isMarkCorrectAnswersEnabled, answerText }) =>
    isMarkCorrectAnswersEnabled && answerText
      ? 'pointer'
      : isMarkCorrectAnswersEnabled
      ? 'not-allowed'
      : 'default'};
  left: 0;
  padding: 0.375rem;
  position: absolute;
  top: -0.1rem;

  > div {
    border-color: ${({ isCorrect, selectedEvent }) =>
      isCorrect ? selectedEvent.colors.secondary : '#bdbdbd'};
    border-radius: 50%;
    border-style: solid;
    border-width: 0.063rem;
    height: 1rem;
    pointer-events: none;
    transition: border-color 100ms ease-in-out;
    width: 1rem;
    svg {
      bottom: 0.188rem;
      left: 0.125rem;
      pointer-events: none;
      position: relative;
      width: 0.688rem;
    }
  }
`;

const AnswerTextInput = styled(TextInput)`
  background-color: transparent;
  height: 1.625rem;
  margin-left: 0.375rem;
`;

const ReorderAnswer = styled(motion.div).attrs({
  animate: { opacity: 1 },
  initial: { opacity: 0 },
  exit: { opacity: 0 }
})`
  cursor: ns-resize;
  display: flex;
  padding: 0.375rem;
  position: absolute;
  left: -0.9rem;
  top: 0.1rem;

  > * {
    pointer-events: none;
  }
`;

const DeleteAnswerWrapper = styled(motion.div).attrs({
  animate: { opacity: 1 },
  initial: { opacity: 0 },
  exit: { opacity: 0 },
  whileHover: { scale: 1.2 },
  whileTap: { scale: 0.9 }
})`
  cursor: pointer;
  display: flex;
  padding: 0.375rem;
  position: absolute;
  right: -1.625rem;
  top: 0.2rem;

  > * {
    pointer-events: none;
  }
`;

export default Answer;
